import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Download } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const API = `${BACKEND_URL}/api`;

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#a855f7", "#ef4444", "#3b82f6"];

const formatCurrency = (val) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val || 0);

// Helper for timeframe filtering
function filterDataByTimeframe(data, timeframe, customStart = "", customEnd = "") {
  if (timeframe === "ALL") return data;
  
  if (timeframe === "CUSTOM") {
    if (!customStart && !customEnd) return data;
    return data.filter(d => {
      const m = d.audit_month || "";
      if (customStart && m < customStart) return false;
      if (customEnd && m > customEnd) return false;
      return true;
    });
  }

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  
  if (timeframe === "YTD") {
    return data.filter(d => (d.audit_month || "").startsWith(currentYear.toString()));
  } else if (timeframe === "LAST_YEAR") {
    const lastYear = (currentYear - 1).toString();
    return data.filter(d => (d.audit_month || "").startsWith(lastYear));
  } else if (timeframe === "LAST_MONTH") {
    const d = new Date(currentYear, currentMonth - 1, 1);
    const target = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    return data.filter(d => (d.audit_month || "") === target);
  } else if (timeframe === "LAST_QUARTER") {
    const currentQuarter = Math.floor(currentMonth / 3);
    const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
    const yearOfLastQuarter = currentQuarter === 0 ? currentYear - 1 : currentYear;
    const startMonthIndex = lastQuarter * 3;
    const targets = [];
    for (let i = 0; i < 3; i++) {
      targets.push(`${yearOfLastQuarter}-${String(startMonthIndex + i + 1).padStart(2, '0')}`);
    }
    return data.filter(d => targets.includes(d.audit_month || ""));
  }
  return data;
}

const getOutflow = (session) => {
  const o = session.outflow || {};
  const comm = session.total_commission || 0;
  const maintenance = o.maintenance || 0;
  const gst = o.gst || 0;
  const staff = o.staff_salary || 0;
  const tech = o.tech || 0;
  const misc = o.miscellaneous || 0;
  const total = maintenance + gst + staff + tech + misc + comm;
  return { maintenance, gst, staff, tech, misc, commission: comm, total };
};

export default function AnalyticsDashboard() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dataMode, setDataMode] = useState("INFLOW"); // "INFLOW" | "OUTFLOW" | "NET_PROFIT"

  // Standard Filters
  const [propertyFilter, setPropertyFilter] = useState("ALL");
  const [sourceFilters, setSourceFilters] = useState(["ALL"]);
  const [timeframeFilter, setTimeframeFilter] = useState("ALL");

  // Compare Mode State (Only for INFLOW)
  const [isCompareMode, setIsCompareMode] = useState(false);
  const [compareType, setCompareType] = useState("SOURCES"); 
  
  const [compareSourceA, setCompareSourceA] = useState("ALL");
  const [compareSourceB, setCompareSourceB] = useState("");
  const [compareTimeframeA, setCompareTimeframeA] = useState("YTD");
  const [compareTimeframeB, setCompareTimeframeB] = useState("LAST_YEAR");
  const [compareTimeframeFilter, setCompareTimeframeFilter] = useState("ALL");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await axios.get(`${API}/analytics`);
        const sorted = (res.data.data || []).sort((a, b) => (a.audit_month || "").localeCompare(b.audit_month || ""));
        setData(sorted);
        const s = new Set();
        sorted.forEach(d => Object.keys(d.by_source || {}).forEach(k => s.add(k)));
        const srcList = [...s].sort();
        if (srcList.length > 0) setCompareSourceB(srcList[0]);
      } catch (err) {
        console.error("Analytics fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const properties = useMemo(() => [...new Set(data.map(d => d.property_name))].filter(Boolean), [data]);
  const sources = useMemo(() => {
    const s = new Set();
    data.forEach(d => Object.keys(d.by_source || {}).forEach(k => s.add(k)));
    return [...s].sort();
  }, [data]);

  const filteredData = useMemo(() => {
    let filtered = filterDataByTimeframe(data, timeframeFilter, customStart, customEnd);
    if (propertyFilter !== "ALL") {
      filtered = filtered.filter(d => d.property_name === propertyFilter);
    }
    return filtered;
  }, [data, propertyFilter, timeframeFilter]);

  // INFLOW Metrics
  const inflowMetrics = useMemo(() => {
    let totalGross = 0, totalNet = 0, totalCommission = 0;
    filteredData.forEach(session => {
      if (sourceFilters.includes("ALL")) {
        totalGross += session.total_gross || 0;
        totalNet += session.total_revenue || 0;
        totalCommission += session.total_commission || 0;
      } else {
        sourceFilters.forEach(src => {
          const srcData = (session.by_source || {})[src];
          if (srcData) {
            totalGross += srcData.gross || 0;
            totalNet += srcData.revenue || 0;
            totalCommission += srcData.commission || 0;
          }
        });
      }
    });
    const blendedCommissionPct = totalGross > 0 ? (totalCommission / totalGross) * 100 : 0;
    return { totalGross, totalNet, totalCommission, blendedCommissionPct };
  }, [filteredData, sourceFilters]);

  const inflowChartData = useMemo(() => {
    const monthlyMap = {};
    filteredData.forEach(session => {
      const m = session.audit_month;
      if (!monthlyMap[m]) monthlyMap[m] = { month: m, gross: 0, net: 0, commission: 0 };
      
      if (sourceFilters.includes("ALL")) {
        monthlyMap[m].gross += session.total_gross || 0;
        monthlyMap[m].net += session.total_revenue || 0;
        monthlyMap[m].commission += session.total_commission || 0;
      } else {
        sourceFilters.forEach(src => {
          const srcData = (session.by_source || {})[src];
          if (srcData) {
            monthlyMap[m].gross += srcData.gross || 0;
            monthlyMap[m].net += srcData.revenue || 0;
            monthlyMap[m].commission += srcData.commission || 0;
          }
        });
      }
    });
    return Object.values(monthlyMap).sort((a, b) => (a.month || "").localeCompare(b.month || ""));
  }, [filteredData, sourceFilters]);

  const inflowPieData = useMemo(() => {
    if (!sourceFilters.includes("ALL") && sourceFilters.length < 2) return [];
    const sourceMap = {};
    filteredData.forEach(session => {
      Object.entries(session.by_source || {}).forEach(([src, metrics]) => {
        if (sourceFilters.includes("ALL") || sourceFilters.includes(src)) {
          if (!sourceMap[src]) sourceMap[src] = 0;
          sourceMap[src] += metrics.commission || 0;
        }
      });
    });
    return Object.entries(sourceMap)
      .filter(([_, val]) => val > 0)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredData, sourceFilters]);

  // COMPARE LOGIC (INFLOW ONLY)
  const compareData = useMemo(() => {
    if (!isCompareMode || dataMode !== "INFLOW") return null;
    let baseData = data;
    if (propertyFilter !== "ALL") {
      baseData = baseData.filter(d => d.property_name === propertyFilter);
    }

    if (compareType === "SOURCES") {
      const tfData = filterDataByTimeframe(baseData, compareTimeframeFilter);
      let aGross=0, aNet=0, aComm=0;
      let bGross=0, bNet=0, bComm=0;
      const chartMap = {};

      tfData.forEach(session => {
        const m = session.audit_month;
        if (!chartMap[m]) chartMap[m] = { month: m, aNet: 0, bNet: 0 };

        if (compareSourceA === "ALL") {
          aGross += session.total_gross || 0;
          aNet += session.total_revenue || 0;
          aComm += session.total_commission || 0;
          chartMap[m].aNet += session.total_revenue || 0;
        } else {
          const srcA = (session.by_source || {})[compareSourceA];
          if (srcA) {
            aGross += srcA.gross || 0;
            aNet += srcA.revenue || 0;
            aComm += srcA.commission || 0;
            chartMap[m].aNet += srcA.revenue || 0;
          }
        }

        if (compareSourceB === "ALL") {
          bGross += session.total_gross || 0;
          bNet += session.total_revenue || 0;
          bComm += session.total_commission || 0;
          chartMap[m].bNet += session.total_revenue || 0;
        } else {
          const srcB = (session.by_source || {})[compareSourceB];
          if (srcB) {
            bGross += srcB.gross || 0;
            bNet += srcB.revenue || 0;
            bComm += srcB.commission || 0;
            chartMap[m].bNet += srcB.revenue || 0;
          }
        }
      });
      return {
        metricsA: { gross: aGross, net: aNet, comm: aComm, pct: aGross>0 ? (aComm/aGross)*100 : 0 },
        metricsB: { gross: bGross, net: bNet, comm: bComm, pct: bGross>0 ? (bComm/bGross)*100 : 0 },
        chart: Object.values(chartMap).sort((a,b)=>a.month.localeCompare(b.month))
      };
    } else {
      const tfAData = filterDataByTimeframe(baseData, compareTimeframeA);
      const tfBData = filterDataByTimeframe(baseData, compareTimeframeB);

      const aggregate = (dataset) => {
        let gross=0, net=0, comm=0;
        dataset.forEach(session => {
          if (compareSourceA === "ALL") {
            gross += session.total_gross || 0;
            net += session.total_revenue || 0;
            comm += session.total_commission || 0;
          } else {
            const src = (session.by_source || {})[compareSourceA];
            if (src) {
              gross += src.gross || 0;
              net += src.revenue || 0;
              comm += src.commission || 0;
            }
          }
        });
        return { gross, net, comm, pct: gross>0 ? (comm/gross)*100 : 0 };
      };
      
      const metricsA = aggregate(tfAData);
      const metricsB = aggregate(tfBData);

      const chart = [
        { name: "Gross Revenue", [compareTimeframeA]: metricsA.gross, [compareTimeframeB]: metricsB.gross },
        { name: "Net Revenue", [compareTimeframeA]: metricsA.net, [compareTimeframeB]: metricsB.net },
        { name: "Commission", [compareTimeframeA]: metricsA.comm, [compareTimeframeB]: metricsB.comm }
      ];

      return { metricsA, metricsB, chart };
    }
  }, [data, isCompareMode, dataMode, compareType, propertyFilter, compareSourceA, compareSourceB, compareTimeframeA, compareTimeframeB, compareTimeframeFilter]);

  // OUTFLOW Metrics
  const outflowMetrics = useMemo(() => {
    let t = 0, maint = 0, gst = 0, staff = 0, tech = 0, misc = 0, comm = 0;
    filteredData.forEach(session => {
      const o = getOutflow(session);
      t += o.total;
      maint += o.maintenance;
      gst += o.gst;
      staff += o.staff;
      tech += o.tech;
      misc += o.misc;
      comm += o.commission;
    });
    return { total: t, maintenance: maint, gst, staff, tech, misc, commission: comm };
  }, [filteredData]);

  const outflowChartData = useMemo(() => {
    const monthlyMap = {};
    filteredData.forEach(session => {
      const m = session.audit_month;
      if (!monthlyMap[m]) monthlyMap[m] = { month: m, total: 0 };
      const o = getOutflow(session);
      monthlyMap[m].total += o.total;
    });
    return Object.values(monthlyMap).sort((a, b) => a.month.localeCompare(b.month));
  }, [filteredData]);

  const outflowPieData = useMemo(() => {
    const m = outflowMetrics;
    const arr = [
      { name: "Commission", value: m.commission },
      { name: "Maintenance", value: m.maintenance },
      { name: "GST", value: m.gst },
      { name: "Staff Salary", value: m.staff },
      { name: "Tech", value: m.tech },
      { name: "Miscellaneous", value: m.misc }
    ];
    return arr.filter(x => x.value > 0).sort((a, b) => b.value - a.value);
  }, [outflowMetrics]);

  // NET PROFIT Metrics
  const netProfitMetrics = useMemo(() => {
    let totalInflow = 0;
    let totalOutflow = 0;
    let totalHmsIncome = 0;
    let totalPartnerIncome = 0;
    filteredData.forEach(session => {
      totalInflow += session.total_gross || 0; // Gross Revenue
      totalOutflow += getOutflow(session).total;
      
      const profit = (session.total_gross || 0) - getOutflow(session).total;
      const hmsIncome = session.phase_results?.phase6?.settlement?.total_hms_commission || 0;
      
      if (session.is_partner_property) {
        totalHmsIncome += hmsIncome;
        totalPartnerIncome += (profit - hmsIncome);
      } else {
        totalHmsIncome += profit;
      }
    });
    const netProfit = totalInflow - totalOutflow;
    const margin = totalInflow > 0 ? (netProfit / totalInflow) * 100 : 0;
    return { totalInflow, totalOutflow, netProfit, margin, totalHmsIncome, totalPartnerIncome };
  }, [filteredData]);

  const netProfitChartData = useMemo(() => {
    const monthlyMap = {};
    filteredData.forEach(session => {
      const m = session.audit_month;
      if (!monthlyMap[m]) monthlyMap[m] = { month: m, inflow: 0, outflow: 0, profit: 0, hmsIncome: 0, partnerIncome: 0 };
      const inflow = session.total_gross || 0;
      const outflow = getOutflow(session).total;
      const profit = inflow - outflow;
      
      const hmsIncome = session.phase_results?.phase6?.settlement?.total_hms_commission || 0;
      const partnerIncome = session.is_partner_property ? (profit - hmsIncome) : 0;
      const actualHmsIncome = session.is_partner_property ? hmsIncome : profit;

      monthlyMap[m].inflow += inflow;
      monthlyMap[m].outflow += outflow;
      monthlyMap[m].profit += profit;
      monthlyMap[m].hmsIncome += actualHmsIncome;
      monthlyMap[m].partnerIncome += partnerIncome;
    });
    return Object.values(monthlyMap).sort((a, b) => a.month.localeCompare(b.month));
  }, [filteredData]);


  const handleExport = () => {
    let csv = "Month,Property,Source,Gross Revenue,Net Revenue,Commission\n";
    filteredData.forEach(d => {
      if (sourceFilters.includes("ALL")) {
        csv += `${d.audit_month},${d.property_name},ALL,${d.total_gross},${d.total_revenue},${d.total_commission}\n`;
      } else {
        sourceFilters.forEach(src => {
          const srcD = (d.by_source || {})[src] || {};
          csv += `${d.audit_month},${d.property_name},${src},${srcD.gross || 0},${srcD.revenue || 0},${srcD.commission || 0}\n`;
        });
      }
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', 'analytics_export.csv');
    a.click();
  };

  if (loading) return <div className="p-8 text-center text-muted-foreground animate-pulse">Loading Analytics Engine...</div>;

  const renderTimeframeOptions = () => (
    <>
      <SelectItem value="ALL">All Time</SelectItem>
      <SelectItem value="LAST_MONTH">Last Month</SelectItem>
      <SelectItem value="LAST_QUARTER">Last Quarter</SelectItem>
      <SelectItem value="YTD">Year to Date (YTD)</SelectItem>
      <SelectItem value="LAST_YEAR">Last Year</SelectItem>
      <SelectItem value="CUSTOM">Custom Range</SelectItem>
    </>
  );

  const renderSourceOptions = () => (
    <>
      <SelectItem value="ALL">All Sources</SelectItem>
      {sources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
    </>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start md:items-center flex-col md:flex-row gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Revenue Analytics</h1>
          <p className="text-muted-foreground mt-1">Deep dive into historical performance, outflows, and profitability.</p>
        </div>
        
        <Tabs value={dataMode} onValueChange={setDataMode} className="w-[400px]">
          <TabsList className="grid w-full grid-cols-3 bg-muted/50 p-1">
            <TabsTrigger value="INFLOW">Inflow</TabsTrigger>
            <TabsTrigger value="OUTFLOW">Outflow</TabsTrigger>
            <TabsTrigger value="NET_PROFIT">Net Profit</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Filters Area */}
      <div className="flex flex-col gap-4 bg-card p-5 rounded-lg border shadow-sm">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Property</label>
            <Select value={propertyFilter} onValueChange={setPropertyFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Properties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Company-wise (All)</SelectItem>
                {properties.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {(!isCompareMode || dataMode !== "INFLOW") && (
            <div className="flex gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Timeframe</label>
                <Select value={timeframeFilter} onValueChange={setTimeframeFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="All Time" />
                  </SelectTrigger>
                  <SelectContent>
                    {renderTimeframeOptions()}
                  </SelectContent>
                </Select>
              </div>
              
              {timeframeFilter === "CUSTOM" && (
                <div className="flex gap-2 items-end">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Start Month</label>
                    <input type="month" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">End Month</label>
                    <input type="month" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
                  </div>
                </div>
              )}
            </div>
          )}

          {dataMode === "INFLOW" && (
            <div className="flex items-center gap-3 ml-4 bg-muted/50 px-4 py-2.5 rounded-full border shadow-sm">
              <Label htmlFor="compare-mode" className="text-sm font-semibold cursor-pointer">Compare Mode</Label>
              <Switch id="compare-mode" checked={isCompareMode} onCheckedChange={setIsCompareMode} />
            </div>
          )}

          {dataMode === "INFLOW" && !isCompareMode && (
            <div className="flex-grow flex justify-end">
              <Button variant="outline" size="sm" onClick={handleExport} className="flex items-center gap-2">
                <Download className="w-4 h-4" /> Export CSV
              </Button>
            </div>
          )}
        </div>

        {/* Dynamic Filters depending on mode */}
        {dataMode === "INFLOW" && !isCompareMode && (
          <div className="space-y-3 mt-2 pt-4 border-t">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Select Sources / OTAs</label>
            <div className="flex flex-wrap gap-2">
              <Badge 
                variant={sourceFilters.includes("ALL") ? "default" : "secondary"} 
                className={`cursor-pointer hover:bg-primary/90 text-sm py-1 px-3 ${sourceFilters.includes("ALL") ? "" : "bg-muted text-muted-foreground"}`}
                onClick={() => setSourceFilters(["ALL"])}
              >
                All Sources
              </Badge>
              {sources.map(s => {
                const isActive = sourceFilters.includes(s) && !sourceFilters.includes("ALL");
                return (
                  <Badge 
                    key={s}
                    variant={isActive ? "default" : "secondary"} 
                    className={`cursor-pointer hover:bg-primary/90 text-sm py-1 px-3 transition-all ${isActive ? "shadow-sm" : "bg-muted text-muted-foreground"}`}
                    onClick={() => {
                      let newFilters = sourceFilters.filter(f => f !== "ALL");
                      if (newFilters.includes(s)) {
                        newFilters = newFilters.filter(f => f !== s);
                      } else {
                        newFilters.push(s);
                      }
                      if (newFilters.length === 0) newFilters = ["ALL"];
                      setSourceFilters(newFilters);
                    }}
                  >
                    {s}
                  </Badge>
                );
              })}
            </div>
          </div>
        )}

        {dataMode === "INFLOW" && isCompareMode && (
          <div className="flex flex-wrap gap-5 items-end mt-2 pt-4 border-t bg-slate-50/50 p-4 rounded-md">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-primary uppercase tracking-wider">Compare Type</label>
              <Select value={compareType} onValueChange={setCompareType}>
                <SelectTrigger className="w-[220px] border-primary/50 bg-white shadow-sm">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SOURCES">Compare Two Sources</SelectItem>
                  <SelectItem value="TIMEFRAMES">Compare Two Timeframes</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="w-px h-10 bg-border mx-2 hidden md:block"></div>

            {compareType === "SOURCES" ? (
              <>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Source A</label>
                  <Select value={compareSourceA} onValueChange={setCompareSourceA}>
                    <SelectTrigger className="w-[180px] bg-white border-blue-200">
                      <SelectValue placeholder="Source A" />
                    </SelectTrigger>
                    <SelectContent>{renderSourceOptions()}</SelectContent>
                  </Select>
                </div>
                <div className="text-muted-foreground text-sm pb-2 font-medium">vs</div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Source B</label>
                  <Select value={compareSourceB} onValueChange={setCompareSourceB}>
                    <SelectTrigger className="w-[180px] bg-white border-green-200">
                      <SelectValue placeholder="Source B" />
                    </SelectTrigger>
                    <SelectContent>{renderSourceOptions()}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 ml-auto flex gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">In Timeframe</label>
                    <Select value={compareTimeframeFilter} onValueChange={setCompareTimeframeFilter}>
                      <SelectTrigger className="w-[180px] bg-white">
                        <SelectValue placeholder="Timeframe" />
                      </SelectTrigger>
                      <SelectContent>{renderTimeframeOptions()}</SelectContent>
                    </Select>
                  </div>
                  {compareTimeframeFilter === "CUSTOM" && (
                    <div className="flex gap-2 items-end">
                      <input type="month" value={customStart} onChange={(e) => setCustomStart(e.target.value)} title="Start Month" className="flex h-10 w-[140px] rounded-md border border-input bg-background px-3 py-2 text-sm" />
                      <input type="month" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} title="End Month" className="flex h-10 w-[140px] rounded-md border border-input bg-background px-3 py-2 text-sm" />
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Timeframe A</label>
                  <Select value={compareTimeframeA} onValueChange={setCompareTimeframeA}>
                    <SelectTrigger className="w-[180px] bg-white border-blue-200">
                      <SelectValue placeholder="Timeframe A" />
                    </SelectTrigger>
                    <SelectContent>{renderTimeframeOptions()}</SelectContent>
                  </Select>
                </div>
                <div className="text-muted-foreground text-sm pb-2 font-medium">vs</div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Timeframe B</label>
                  <Select value={compareTimeframeB} onValueChange={setCompareTimeframeB}>
                    <SelectTrigger className="w-[180px] bg-white border-green-200">
                      <SelectValue placeholder="Timeframe B" />
                    </SelectTrigger>
                    <SelectContent>{renderTimeframeOptions()}</SelectContent>
                  </Select>
                </div>
                {(compareTimeframeA === "CUSTOM" || compareTimeframeB === "CUSTOM") && (
                  <div className="flex gap-2 items-end">
                    <input type="month" value={customStart} onChange={(e) => setCustomStart(e.target.value)} title="Start Month" className="flex h-10 w-[140px] rounded-md border border-input bg-background px-3 py-2 text-sm" />
                    <input type="month" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} title="End Month" className="flex h-10 w-[140px] rounded-md border border-input bg-background px-3 py-2 text-sm" />
                  </div>
                )}
                <div className="space-y-1.5 ml-auto">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">For Source</label>
                  <Select value={compareSourceA} onValueChange={setCompareSourceA}>
                    <SelectTrigger className="w-[180px] bg-white">
                      <SelectValue placeholder="Source" />
                    </SelectTrigger>
                    <SelectContent>{renderSourceOptions()}</SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* DASHBOARDS */}
      
      {/* INFLOW DASHBOARD */}
      {dataMode === "INFLOW" && !isCompareMode && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Gross Revenue</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold">{formatCurrency(inflowMetrics.totalGross)}</div></CardContent></Card>
            <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Net Revenue</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold text-green-600">{formatCurrency(inflowMetrics.totalNet)}</div></CardContent></Card>
            <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Total Commission</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold text-red-500">{formatCurrency(inflowMetrics.totalCommission)}</div></CardContent></Card>
            <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Blended Commission %</CardTitle></CardHeader><CardContent><div className="text-2xl font-bold">{inflowMetrics.blendedCommissionPct.toFixed(2)}%</div></CardContent></Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-lg">MoM Trend (Gross vs Net)</CardTitle></CardHeader>
              <CardContent className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={inflowChartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={(val) => `₹${(val/1000)}k`} />
                    <Tooltip formatter={(val) => formatCurrency(val)} />
                    <Legend />
                    <Line type="monotone" dataKey="gross" name="Gross" stroke="#3b82f6" strokeWidth={2} />
                    <Line type="monotone" dataKey="net" name="Net" stroke="#10b981" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {sourceFilters.includes("ALL") || sourceFilters.length > 1 ? (
              <Card>
                <CardHeader><CardTitle className="text-lg">Commission Breakdown by OTA</CardTitle></CardHeader>
                <CardContent className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={inflowPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={110}>
                        {inflowPieData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(val) => formatCurrency(val)} />
                      <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{ fontSize: '11px', maxWidth: '35%' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader><CardTitle className="text-lg">Commission Deductions (MoM)</CardTitle></CardHeader>
                <CardContent className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={inflowChartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(val) => `₹${(val/1000)}k`} />
                      <Tooltip formatter={(val) => formatCurrency(val)} />
                      <Bar dataKey="commission" name="Commission Paid" fill="#ef4444" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}

      {/* COMPARE DASHBOARD */}
      {dataMode === "INFLOW" && isCompareMode && compareData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-blue-200 shadow-sm bg-blue-50/20">
              <CardHeader className="bg-blue-50/50 pb-4 border-b">
                <CardTitle className="text-blue-800 text-lg flex items-center justify-between">
                  {compareType === "SOURCES" ? compareSourceA : compareTimeframeA}
                  <span className="text-xs font-normal text-blue-600 bg-blue-100 px-2 py-1 rounded-full">Dataset A</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground font-medium mb-1">Net Revenue</div>
                    <div className="text-3xl font-bold text-slate-800">{formatCurrency(compareData.metricsA.net)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground font-medium mb-1">Commission %</div>
                    <div className="text-3xl font-bold text-slate-800">{compareData.metricsA.pct.toFixed(2)}%</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-green-200 shadow-sm bg-green-50/20">
              <CardHeader className="bg-green-50/50 pb-4 border-b">
                <CardTitle className="text-green-800 text-lg flex items-center justify-between">
                  {compareType === "SOURCES" ? compareSourceB : compareTimeframeB}
                  <span className="text-xs font-normal text-green-700 bg-green-100 px-2 py-1 rounded-full">Dataset B</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground font-medium mb-1">Net Revenue</div>
                    <div className="text-3xl font-bold text-slate-800">{formatCurrency(compareData.metricsB.net)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground font-medium mb-1">Commission %</div>
                    <div className="text-3xl font-bold text-slate-800">{compareData.metricsB.pct.toFixed(2)}%</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="shadow-sm">
            <CardHeader className="border-b bg-slate-50/50">
              <CardTitle className="text-lg">
                {compareType === "SOURCES" ? "Net Revenue Trend Comparison" : "Total Metrics Comparison"}
              </CardTitle>
            </CardHeader>
            <CardContent className="h-[400px] pt-6">
              <ResponsiveContainer width="100%" height="100%">
                {compareType === "SOURCES" ? (
                  <LineChart data={compareData.chart}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{fill: '#64748b'}} />
                    <YAxis tickFormatter={(val) => `₹${(val/1000)}k`} tick={{fill: '#64748b'}} />
                    <Tooltip formatter={(val) => formatCurrency(val)} contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'}} />
                    <Legend wrapperStyle={{paddingTop: '20px'}} />
                    <Line type="monotone" dataKey="aNet" name={compareSourceA} stroke="#3b82f6" strokeWidth={3} dot={{r: 4, strokeWidth: 2}} activeDot={{r: 6}} />
                    <Line type="monotone" dataKey="bNet" name={compareSourceB} stroke="#10b981" strokeWidth={3} dot={{r: 4, strokeWidth: 2}} activeDot={{r: 6}} />
                  </LineChart>
                ) : (
                  <BarChart data={compareData.chart}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{fill: '#64748b', fontWeight: 500}} />
                    <YAxis tickFormatter={(val) => `₹${(val/1000)}k`} tick={{fill: '#64748b'}} />
                    <Tooltip formatter={(val) => formatCurrency(val)} cursor={{fill: '#f1f5f9'}} contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'}} />
                    <Legend wrapperStyle={{paddingTop: '20px'}} />
                    <Bar dataKey={compareTimeframeA} fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={40} />
                    <Bar dataKey={compareTimeframeB} fill="#10b981" radius={[4, 4, 0, 0]} barSize={40} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </>
      )}

      {/* OUTFLOW DASHBOARD */}
      {dataMode === "OUTFLOW" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <Card className="col-span-2 md:col-span-1 border-orange-200 bg-orange-50/30">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-orange-800">Total Outflow</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-bold text-orange-600">{formatCurrency(outflowMetrics.total)}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Commission (Tool)</CardTitle></CardHeader>
              <CardContent><div className="text-lg font-bold">{formatCurrency(outflowMetrics.commission)}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Maintenance</CardTitle></CardHeader>
              <CardContent><div className="text-lg font-bold">{formatCurrency(outflowMetrics.maintenance)}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">GST</CardTitle></CardHeader>
              <CardContent><div className="text-lg font-bold">{formatCurrency(outflowMetrics.gst)}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Staff Salary</CardTitle></CardHeader>
              <CardContent><div className="text-lg font-bold">{formatCurrency(outflowMetrics.staff)}</div></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Tech & Misc</CardTitle></CardHeader>
              <CardContent><div className="text-lg font-bold">{formatCurrency(outflowMetrics.tech + outflowMetrics.misc)}</div></CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-lg">Total Outflow (MoM)</CardTitle></CardHeader>
              <CardContent className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={outflowChartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={(val) => `₹${(val/1000)}k`} />
                    <Tooltip formatter={(val) => formatCurrency(val)} />
                    <Bar dataKey="total" name="Total Outflow" fill="#f97316" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-lg">Cost Distribution</CardTitle></CardHeader>
              <CardContent className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={outflowPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={110} label={({name, percent}) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                      {outflowPieData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(val) => formatCurrency(val)} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* NET PROFIT DASHBOARD */}
      {dataMode === "NET_PROFIT" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <Card className="col-span-2 md:col-span-1"><CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Total Inflow</CardTitle></CardHeader><CardContent><div className="text-lg font-bold text-green-600">{formatCurrency(netProfitMetrics.totalInflow)}</div></CardContent></Card>
            <Card className="col-span-2 md:col-span-1"><CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Total Outflow</CardTitle></CardHeader><CardContent><div className="text-lg font-bold text-orange-600">{formatCurrency(netProfitMetrics.totalOutflow)}</div></CardContent></Card>
            <Card className="col-span-2 md:col-span-1 border-slate-200">
              <CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-slate-700">Property Net Profit</CardTitle></CardHeader>
              <CardContent><div className={`text-lg font-bold ${netProfitMetrics.netProfit >= 0 ? "text-slate-800" : "text-red-600"}`}>{formatCurrency(netProfitMetrics.netProfit)}</div></CardContent>
            </Card>
            <Card className="col-span-2 md:col-span-1 border-violet-200 bg-violet-50/30">
              <CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-violet-800">Total HMS Income</CardTitle></CardHeader>
              <CardContent><div className="text-xl font-bold text-violet-600">{formatCurrency(netProfitMetrics.totalHmsIncome)}</div></CardContent>
            </Card>
            <Card className="col-span-2 md:col-span-1 border-blue-200 bg-blue-50/30">
              <CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-blue-800">Partner Income</CardTitle></CardHeader>
              <CardContent><div className="text-lg font-bold text-blue-600">{formatCurrency(netProfitMetrics.totalPartnerIncome)}</div></CardContent>
            </Card>
            <Card className="col-span-2 md:col-span-1"><CardHeader className="pb-2"><CardTitle className="text-xs font-medium text-muted-foreground">Profit Margin</CardTitle></CardHeader><CardContent><div className="text-lg font-bold">{netProfitMetrics.margin.toFixed(1)}%</div></CardContent></Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-lg">Inflow vs Outflow (MoM)</CardTitle></CardHeader>
              <CardContent className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={netProfitChartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={(val) => `₹${(val/1000)}k`} />
                    <Tooltip formatter={(val) => formatCurrency(val)} />
                    <Legend />
                    <Line type="monotone" dataKey="inflow" name="Inflow (Net Rev)" stroke="#10b981" strokeWidth={3} />
                    <Line type="monotone" dataKey="outflow" name="Outflow" stroke="#f97316" strokeWidth={3} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-lg">Net Profit Trend (MoM)</CardTitle></CardHeader>
              <CardContent className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={netProfitChartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={(val) => `₹${(val/1000)}k`} />
                    <Tooltip formatter={(val) => formatCurrency(val)} />
                    <Legend />
                    <Bar dataKey="hmsIncome" name="HMS Income" fill="#8b5cf6" stackId="a" />
                    <Bar dataKey="partnerIncome" name="Partner Income" fill="#3b82f6" stackId="a" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </>
      )}

    </div>
  );
}
